import * as Primitive from '@radix-ui/react-select';

import { cn, POPOVER_MOTION } from '../lib/cn';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from './icons';

export interface ISelectOption<TValue extends string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface ISelectProps<TValue extends string> {
  value: TValue;
  onChange: (next: TValue) => void;
  options: readonly ISelectOption<TValue>[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export const Select = <TValue extends string>({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  ...rest
}: ISelectProps<TValue>) => (
  <Primitive.Root value={value} onValueChange={(next) => onChange(next as TValue)}>
    <Primitive.Trigger
      id={id}
      disabled={disabled}
      className={cn(
        'flex h-control w-full items-center gap-3 rounded-control border bg-field px-input',
        'text-left text-body text-primary data-[placeholder]:text-faint',
        'focus:border-border-focus focus:ring-3 focus:ring-focus focus:outline-none',
        disabled ? 'border-border-muted text-muted' : 'border-border-raised',
        className,
      )}
      {...rest}
    >
      <span className="min-w-0 grow truncate">
        <Primitive.Value placeholder={placeholder} />
      </span>
      <Primitive.Icon className="flex shrink-0 text-faint">
        <ChevronDownIcon size={13} />
      </Primitive.Icon>
    </Primitive.Trigger>

    <Primitive.Portal>
      <Primitive.Content
        position="popper"
        sideOffset={6}
        collisionPadding={8}
        className={cn(
          'rounded-panel border border-border-raised bg-surface-raised p-label shadow-panel',
          'w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin)',
          'max-h-(--radix-select-content-available-height)',
          POPOVER_MOTION,
        )}
      >
        <Primitive.ScrollUpButton className="flex h-5 items-center justify-center text-faint">
          <ChevronUpIcon size={13} />
        </Primitive.ScrollUpButton>
        <Primitive.Viewport>
          {options.map((option) => (
            <Primitive.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-item px-inset py-label',
                'text-data text-muted outline-none',
                'data-[highlighted]:bg-hover-raised data-[highlighted]:text-primary',
                'data-[state=checked]:text-primary',
                'data-[disabled]:cursor-default data-[disabled]:opacity-70',
              )}
            >
              <span className="min-w-0 grow">
                <Primitive.ItemText>{option.label}</Primitive.ItemText>
                {option.description && (
                  <span className="mt-tight block text-meta leading-17 text-muted">
                    {option.description}
                  </span>
                )}
              </span>
              <Primitive.ItemIndicator className="flex shrink-0 pt-tight text-accent">
                <CheckIcon size={12} />
              </Primitive.ItemIndicator>
            </Primitive.Item>
          ))}
        </Primitive.Viewport>
        <Primitive.ScrollDownButton className="flex h-5 items-center justify-center text-faint">
          <ChevronDownIcon size={13} />
        </Primitive.ScrollDownButton>
      </Primitive.Content>
    </Primitive.Portal>
  </Primitive.Root>
);
